import { useState, useRef, useEffect, useCallback } from 'react'

import useSentry from 'Pods/Shared/Hooks/useSentry'

import loadScript from './Utils/loadScript'

const useCarousel = ({
  medias,
  onActiveIndexChange,
  activeIndex,
  baseClassName,
  isTransitioning,
}) => {
  const { captureException } = useSentry()
  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const swiperRef = useRef(null)
  const marmosetViewersRef = useRef(new Map())
  const marmosetAbortRef = useRef(null)

  useEffect(() => {
    const activeMedia = medias[activeIndex]

    if (marmosetAbortRef.current) {
      marmosetAbortRef.current.abort = true
      marmosetAbortRef.current = null
    }

    if (activeMedia?.type === 'marmoset') {
      const getMarmosetContainer = () => {
        // Always use document.querySelector with data-src to avoid virtual+loop index issues
        const selector = `.${baseClassName}__other-media[data-type="marmoset"][data-src="${activeMedia.fileUrl}"]`
        return document.querySelector(selector)
      }

      const marmosetContainer = getMarmosetContainer()
      if (!marmosetContainer) {
        return
      }

      const existingViewerEntry = marmosetViewersRef.current.get(activeIndex)

      if (existingViewerEntry) {
        const { viewer: existingViewer, container: existingContainer } = existingViewerEntry || {}
        const viewerDomRoot = existingViewer?.domRoot
        const viewerConnected = !!(viewerDomRoot && viewerDomRoot.isConnected)
        const containerChanged = existingContainer !== marmosetContainer
        const viewerMissingInContainer = viewerDomRoot && !marmosetContainer.contains(viewerDomRoot)

        if (containerChanged || viewerMissingInContainer || !viewerConnected) {
          try {
            if (existingViewer && typeof existingViewer.unload === 'function') {
              existingViewer.unload()
            }
            if (existingContainer && existingContainer.innerHTML !== '') {
              // eslint-disable-next-line no-param-reassign
              existingContainer.innerHTML = ''
            }
          } catch (error) {
            captureException(error, {
              tags: {
                component: 'Carousel',
                action: 'cleanupMarmosetViewer',
              },
              extra: {
                activeIndex,
                fileUrl: activeMedia?.fileUrl,
              },
            })
          }
          marmosetViewersRef.current.delete(activeIndex)
        } else {
          const containerWidth = marmosetContainer.offsetWidth
          const containerHeight = marmosetContainer.offsetHeight

          if (containerWidth && containerHeight && existingViewer && typeof existingViewer.resize === 'function') {
            try {
              existingViewer.resize(containerWidth, containerHeight)
            } catch (error) {
              captureException(error, {
                tags: {
                  component: 'Carousel',
                  action: 'resizeMarmosetViewer',
                },
                extra: {
                  activeIndex,
                  fileUrl: activeMedia?.fileUrl,
                },
              })
            }
          }
        }
      }

      if (!marmosetViewersRef.current.has(activeIndex)) {
        const abortController = { abort: false }
        marmosetAbortRef.current = abortController

        const loadMarmosetSDK = () => {
          if (!window.marmoset) {
            return loadScript('https://viewer.marmoset.co/main/marmoset.js')
              .catch((error) => {
                captureException(error, {
                  tags: {
                    component: 'Carousel',
                    action: 'loadMarmosetSDK',
                  },
                  extra: {
                    activeIndex,
                    fileUrl: activeMedia?.fileUrl,
                  },
                })
              })
          }
          return Promise.resolve()
        }

        const initMarmosetViewer = (attempt = 0) => {
          if (abortController.abort) {
            return
          }

          const container = getMarmosetContainer()

          if (!container) {
            if (attempt < 50) {
              setTimeout(() => initMarmosetViewer(attempt + 1), 100)
            }
            return
          }

          if (window.marmoset && window.marmoset.WebViewer) {
            try {
              const containerWidth = container.offsetWidth
              const containerHeight = container.offsetHeight

              if (!containerWidth || !containerHeight) {
                if (attempt < 50) {
                  setTimeout(() => initMarmosetViewer(attempt + 1), 100)
                }
                return
              }

              if (abortController.abort) {
                return
              }

              const viewer = new window.marmoset.WebViewer(
                containerWidth,
                containerHeight,
                activeMedia.fileUrl,
              )

              container.innerHTML = ''
              container.appendChild(viewer.domRoot)

              // Auto-start the viewer
              if (viewer.loadScene && typeof viewer.loadScene === 'function') {
                viewer.loadScene()
              }

              marmosetViewersRef.current.set(activeIndex, {
                viewer,
                container,
              })
            } catch (error) {
              captureException(error, {
                tags: {
                  component: 'Carousel',
                  action: 'initMarmosetViewer',
                },
                extra: {
                  activeIndex,
                  fileUrl: activeMedia?.fileUrl,
                },
              })
            }
          } else if (attempt < 50) {
            setTimeout(() => initMarmosetViewer(attempt + 1), 100)
          }
        }

        loadMarmosetSDK().then(() => {
          if (!abortController.abort) {
            initMarmosetViewer()
          }
        })
      }
    }
  }, [activeIndex, captureException, isTransitioning])

  const handleSlideChange = useCallback((swiper) => {
    const { realIndex, previousRealIndex } = swiper

    // Handle marmoset viewer cleanup
    if (marmosetViewersRef.current.has(previousRealIndex)) {
      const { viewer, container } = marmosetViewersRef.current.get(previousRealIndex) || {}
      // Unload the marmoset viewer if the marmoset slide is not the active one
      try {
        if (viewer && typeof viewer.unload === 'function') {
          viewer.unload()
        }
        if (container && container.innerHTML !== '') {
          // eslint-disable-next-line no-param-reassign
          container.innerHTML = ''
        }
      } catch (error) {
        captureException(error, {
          tags: {
            component: 'Carousel',
            action: 'cleanupMarmosetViewer',
          },
          extra: {
            previousRealIndex,
          },
        })
      }
      marmosetViewersRef.current.delete(previousRealIndex)
    }

    onActiveIndexChange(realIndex)
  }, [onActiveIndexChange])

  // Cleanup function for Marmoset viewers
  useEffect(() => {
    return () => {
      if (marmosetAbortRef.current) {
        marmosetAbortRef.current.abort = true
        marmosetAbortRef.current = null
      }

      marmosetViewersRef.current.forEach(({ viewer, container }) => {
        try {
          if (viewer && typeof viewer.unload === 'function') {
            viewer.unload()
          }
          if (container && container.innerHTML !== '') {
            // eslint-disable-next-line no-param-reassign
            container.innerHTML = ''
          }
        } catch (error) {
          // Ignore cleanup errors
        }
      })
      marmosetViewersRef.current.clear()
    }
  }, [])

  return {
    thumbsSwiper,
    setThumbsSwiper,
    swiperRef,
    handleSlideChange,
  }
}

export default useCarousel
